import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, OnInit, OnDestroy, NgZone, inject } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router, TitleStrategy } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccessTokenService, ADMIN_CONSOLE_SECTION_KEYS, SpendkeyStaffLevel } from 'src/app/Services/access-token.service';
import { budgetServices } from 'src/app/Services/budget.service';
import { DrawerServiceService } from 'src/app/Services/drawer-service.service';
import { DynamicDataService } from 'src/app/Services/dynamic-data.service';
import { PermissionGateService } from 'src/app/Services/permission-gate.service';
import { SettingsService } from 'src/app/Services/settings.service';
import { TenantConfigService } from 'src/app/Services/tenant-config.service';
import { UserDataProviderService } from 'src/app/Services/user-data-provider.service';
import { MaterialModule } from 'src/app/Shared/MaterialModule/material/material.module';
import { TranslateModule } from '@ngx-translate/core';
// `environment` was only used by the now-commented STATIC_MENU_ITEMS
// (attachments-demo feature flag). Kept commented so restoring that block
// restores its import too.
// import { environment } from 'src/environments/environment';
import { DEV_MENU_ITEMS } from './side-bar.dev-menu';

/**
 * Hosts on which DEV_MENU_ITEMS may be injected. Deployed environments are
 * intentionally excluded — the sidebar must come from getSidebarData alone
 * everywhere except a local dev box. The dev-menu file itself ships empty
 * so this gate is belt-and-suspenders on top of an already-empty payload.
 */
const ALLOWED_SERVERS = [
  'localhost:4200'
];

/**
 * Sidebar icon names that map to files under `assets/svg/<name>.svg`.
 * Registered with MatIconRegistry in the constructor and rendered via
 * <mat-icon svgIcon="…"> so CSS `color` (active/hover/theme) cascades
 * into the SVG. Checked before the icon-font branch so names that share
 * the `sk-side-` prefix (e.g. 'sk-side-budget-dash') win over the font.
 */
export const SVG_SIDEBAR_ICONS = [
  'sk-side-budget-dash',
  'sk-side-mis-dashboard',
  'sk-side-product-service-analysis',
  'sk-side-planning',
] as const;
const SVG_SIDEBAR_ICON_SET = new Set<string>(SVG_SIDEBAR_ICONS);

/**
 * Client-side sidebar entries that aren't yet driven by `getSidebarData`.
 * Appended after the API-derived menu so they appear in the rail without
 * needing a `tblPage` row. Items here graduate to the DB-driven sidebar
 * once the page is ready for general availability.
 *
 * Approval Center, Cost Escalation Dashboard and IntegrityNext now come from
 * the DB-driven sidebar (tblPage / SP_Get_SidebarData) instead of this array.
 * Only the feature-flagged attachments demo remains client-side (it is a
 * build-time-stripped testenv surface, so it never warrants a per-tenant row).
 */
// const STATIC_MENU_ITEMS = [
//   // Evidence-attachments demo surface — only surfaced on env builds that opt in via the
//   // `featureFlags.attachmentsDemo` flag (currently only testenv). Stripped from prod/poc/preprod
//   // builds at compile time because the property is absent from those env files.
//   ...((environment as { featureFlags?: { attachmentsDemo?: boolean } }).featureFlags?.attachmentsDemo === true ? [{
//     title: 'Attachments demo',
//     tooltip: 'Evidence-attachments end-to-end demo — upload, encrypted at rest, scanned by Defender, downloadable via short-lived SAS',
//     icon: 'icon-Document',
//     link: 'attachments-demo',
//     selected: false
//   }] : [])
// ];

/**
 * Sidebar entries that are only appended when the current user is a SuperAdmin
 * (US #9320 / F5 #9348). The entries are pushed via `appendStaticMenuItems()`
 * so they follow the same dedup-by-link rule as the regular static items.
 * The corresponding parent route in `app.routes.ts` is independently gated by
 * `superAdminGuard` — hiding the entry here is a usability gate, not a
 * security boundary.
 */
// const SUPER_ADMIN_MENU_ITEMS = [
//   {
//     title: 'Admin Console',
//     tooltip: 'Audit log, RBAC + SoD, workflow configuration (SuperAdmin only)',
//     icon: 'admin_panel_settings',
//     link: 'admin-console',
//     selected: false
//   }
// ];

/**
 * DB-driven sidebar entries that are additionally gated on an RBAC permission
 * key (task #9888). The entry is hidden while the caller lacks the key — a
 * usability gate only; the route guard and the API's fail-closed persona
 * check are the actual security boundaries. Reactive on the MePermissions
 * slice, so the entry appears once hydration proves the permission.
 */
const PERMISSION_GATED_SIDEBAR_LINKS: ReadonlyArray<{
  link: string;
  permissions: readonly string[];
  /** Explicit grants only: stays hidden on a non-RBAC tenant, where every other entry shows. */
  grantedOnly?: boolean;
}> = [
  { link: 'control-tower', permissions: ['control-tower.view'] },
  { link: 'spend-explorer', permissions: ['spend-explorer.view'] },
  // Spend Reclassification. view is the entry key — edit and approve are each
  // granted alongside it, so holding either implies it.
  { link: 'reclassification', permissions: ['reclassification.view'] },
  // Report Data Uploads is one page over several feeds, each with its own
  // upload key, so the entry survives on ANY of them and is hidden entirely
  // for a caller who can upload nothing.
  { link: 'report-data-uploads',
    permissions: ['pbf.data.upload', 'esg.upload', 'co2.upload'] },
  // Savings Tracker is a shell over several tabs, each with its own key, so the
  // entry survives on ANY of them: `savings.tracker.view` opens Initiatives /
  // Aging / Reports, `opportunity.view` opens the Opportunities tab. A caller
  // holding neither has no reachable tab, so the entry is hidden entirely
  // rather than leading to an empty shell.
  { link: 'savings-tracker', permissions: ['savings.tracker.view', 'opportunity.view'] },
  // Admin Console is a shell over Audit and RBAC & SoD, each with its own key,
  // so the entry survives on ANY of them and is hidden for a caller who can
  // open neither. Workflow is not listed: it is Spendkey-staff only, so it must
  // never be the reason a tenant user sees the entry. Spendkey staff bypass
  // this gate entirely — `superAdminGuard` admits them on email domain.
  { link: 'admin-console', permissions: [...ADMIN_CONSOLE_SECTION_KEYS], grantedOnly: true },
  // Client-facing dashboard over the pna_ tables the AI nuance pipeline writes.
  // Seed its tblPage row on RBAC tenants ONLY: a legacy tenant returns no
  // permissions, so the entry would be hidden here and the route guard would
  // redirect — see savings-recommendations.guard.ts for why that is the chosen
  // handling rather than a frontend legacy-tenant bypass.
  { link: 'savings-recommendations', permissions: ['savings.opportunities.report.view'] },
  // KPI Dictionary is an HtmlDoc page. SP_Get_SidebarData only hides report pages
  // (PbiPage fields), so without this entry every user saw the link while
  // htmlPageGuard refused them on click. Key matches the route's htmlPage data.
  { link: 'kpi-dictionary', permissions: ['kpi-dictionary.view'] },
  // savings-opportunity / savings-opportunity-config are no longer sidebar
  // entries at all — both are chip-tabs on the Savings Tracker shell, gated
  // there by tblPageFields FeatureGate rows plus `opportunity.view` /
  // `opportunity.configure`. Their tblPage rows (54/57) are retired to
  // Status=0 by SavingsTrackerScripts/RESTRUCTURE_03_OpportunityTabGate.sql,
  // so SP_Get_SidebarData stops returning them and there is nothing left here
  // to gate. A transitional redirect in app.routes.ts covers tenants that have
  // not run the script yet.
];

/**
 * DB-driven sidebar entries that are ADDITIONALLY gated on the caller being
 * Spendkey staff, at the same level as the link's route guard
 * (`spendkeyStaffGuard(level)`), so a link is never offered to someone the route
 * would refuse. Tenant admins never see these links even when they hold the
 * underlying `IsPermissionRequired` permission on the tblPage row. This filter is
 * the usability gate; the route guard and the API's `[RequireSpendkeyStaff]` are
 * the enforcement.
 *
 * Future-proofing: if this list grows past ~3 entries, promote to an
 * `IsSpendkeyInternal BIT` column on `tblPage` so the truth lives in the DB.
 */
const INTERNAL_ONLY_SIDEBAR_LINKS = new Map<string, SpendkeyStaffLevel>([
  ['savings-settings', 'any'],
  // Runs the AI pipelines against a tenant database and edits the client→database
  // mapping. Defensive: there is no tblPage row for it today, so it reaches the
  // sidebar only if one is ever seeded.
  ['ai-recommendations', 'any'],
  // Monitors the AI pipeline's job queue across tenants — an operations surface, not
  // a tenant one. Defensive here for the same reason: no tblPage row exists today.
  ['job-monitor', 'any'],
  // Users DOES have a live tblPage row (Id 29), so unlike the two above this entry is
  // the only thing keeping it out of a tenant's sidebar. Note the row carries
  // IsPermissionRequired=1, implying it is grantable per user; that grant can never
  // take effect while the route stays on the staff axis.
  ['users', 'admin'],
  // Seeded only on tenants that have raw.tbl_adhoc_analyses_fct (Yunex); the row alone would
  // show it to every user there, so this entry keeps it to staff admins.
  ['adhoc-enrichment', 'admin'],
]);

@Component({
  selector: 'app-side-bar',
  standalone: true,
  imports: [CommonModule, MaterialModule, TranslateModule],
  templateUrl: './side-bar.component.html',
  styleUrl: './side-bar.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SideBarComponent implements OnInit, OnDestroy {

  private messagesIsOpen = false;
  private expensesIsOpen = false;

  isMenuCollapse: boolean;
  menu: any;
  activeUrl: any;

  aMenuList: any;

  dynamicTableData: any;
  settingsTable: any;
  selectedYr: any;
  createdBgtYr: any;
  toSelect: any;

  clientNm: any = 'Spendkey';
  clientIcon: any = 'assets/logo-white.svg';

  /** Full assembled menu (API + static + dev) before permission gating. */
  private fullMenu: any[] | null = null;
  private deniedLinks = new Set<string>();
  private permGateSub = new Subscription();
  private collapseSub = new Subscription();
  private readonly titleStrategy = inject(TitleStrategy);

  intervalId: any;
  lastActivityTime = Date.now();
  private userActiveTime = 15; // minutes idle before forced logout; overridden by tenant config
  private isRefreshing = false;
  private readonly trackedEvents = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'] as const;
  private activityHandler = () => {
    const now = Date.now();
    if (now - this.lastActivityTime < 1000) return;
    this.lastActivityTime = now;
    localStorage.setItem('lastActivity', String(now));
  };

  constructor(
    private router: Router,
    private accessTokenService: AccessTokenService,
    private dynamicDataService: DynamicDataService,
    private drawerService: DrawerServiceService,
    private budgetService: budgetServices,
    private settingsService: SettingsService,
    private tenantConfig: TenantConfigService,
    private permissionGate: PermissionGateService,
    private UserDataProviderService: UserDataProviderService,
    private ngZone: NgZone,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer
  ) {
    this.messagesIsOpen = false;
    this.isMenuCollapse = true;

    for (const name of SVG_SIDEBAR_ICONS) {
      this.matIconRegistry.addSvgIcon(
        name,
        this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/svg/${name}.svg`)
      );
    }
  }

  isIconFont(icon: string): boolean {
    return typeof icon === 'string' && (icon.startsWith('icon-') || icon.startsWith('spendkey-') || icon.startsWith('sk-side-'));
  }

  isSvgIcon(icon: string): boolean {
    return typeof icon === 'string' && SVG_SIDEBAR_ICON_SET.has(icon);
  }

  ngOnInit() {
    // The header and content offset read the same stream; a sidebar recreated
    // by *ngIf must adopt it rather than its own default or the three disagree.
    this.collapseSub.add(
      this.accessTokenService.castValue.subscribe((collapsed: boolean) => (this.isMenuCollapse = collapsed))
    );

    this.startTracking();
    this.startInterval();

    for (const gate of PERMISSION_GATED_SIDEBAR_LINKS) {
      // ANY of the keys keeps the entry: combineLatest re-evaluates the whole
      // set on every slice change, so a late-arriving key un-hides the entry
      // rather than leaving it denied by whichever key resolved first.
      this.permGateSub.add(
        combineLatest(gate.permissions.map((key) => gate.grantedOnly
          ? this.permissionGate.hasPermissionOnly$(key)
          : this.permissionGate.has$(key)))
          .pipe(map((results) => results.some(Boolean)))
          .subscribe((allowed) => {
            if (allowed) {
              this.deniedLinks.delete(gate.link);
            } else {
              this.deniedLinks.add(gate.link);
            }
            this.applyPermissionGates();
          }));
    }

    this.settingsService.refreshSettings.subscribe((res: any) => {
      if (res === true) {
        this.getSettings();
      }
    });

    this.getSettings();
    this.getUserActiveTime();

    if (this.dynamicDataService.getDataFromLocal('SideBar') != null) {
      const cachedData = this.dynamicDataService.getDataFromLocal('SideBar');
      this.aMenuList = this.buildMenuTree(cachedData);
      this.menu = this.aMenuList;
      this.appendStaticMenuItems();
      this.injectDevMenuItems();
      this.captureMenuForGating();
      const initialPath = this.router.url.replace(/^\//, '').split('?')[0];
      this.selectMenuWithLink(initialPath || 'home');
    } else {
      this.getSidebarData();
    }

    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.activeUrl = this.router.url;
        const path = this.activeUrl.replace(/^\//, '').split('?')[0];
        this.selectMenuWithLink(path || 'home');
        this.drawerService.closeDrawer();
      }
    });
  }

  startInterval() {
    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => {
        const isTokenExpired = this.accessTokenService.isTokenExpired();
        if (isTokenExpired === true) {
          if (this.isUserActive()) {
            if (!this.isRefreshing) {
              this.ngZone.run(() => this.refreshAccessToken());
            }
          } else if (window.location.pathname !== '/login') {
            this.ngZone.run(() => {
              this.accessTokenService.clearTokens();
              this.UserDataProviderService.isShowSidebar.emit(false);
              this.router.navigate(['login']);
            });
          }
        }
      }, 2000);
    });
  }

  refreshAccessToken() {
    this.isRefreshing = true;
    this.UserDataProviderService.getRefreshToken({ UserEmail: this.accessTokenService.getUserEmail() })
      .subscribe({
        next: (res: any) => {
          if (res?.data) {
            this.accessTokenService.setTokenInLocalStorage(res.data);
          } else {
            this.accessTokenService.clearTokens();
            this.UserDataProviderService.isShowSidebar.emit(false);
            this.router.navigate(['login']);
          }
          this.isRefreshing = false;
        },
        error: () => {
          this.isRefreshing = false;
          this.accessTokenService.clearTokens();
          this.UserDataProviderService.isShowSidebar.emit(false);
          this.router.navigate(['login']);
        }
      });
  }

  getUserActiveTime() {
    this.settingsService.getUserActiveTime().subscribe((res: any) => {
      if (res.status === 'success') {
        const raw = Number(res.data[0].settingValue);
        if (Number.isFinite(raw) && raw > 0) {
          this.userActiveTime = raw;
        }
      }
    });
  }

  startTracking() {
    this.trackedEvents.forEach(e => window.addEventListener(e, this.activityHandler, { passive: true }));
  }

  isUserActive(): boolean {
    const raw = localStorage.getItem('lastActivity');
    if (!raw) return false;
    const last = Number(raw);
    if (!Number.isFinite(last)) return false;
    return (Date.now() - last) < this.userActiveTime * 60 * 1000;
  }

  collapseMenu(): void {
    this.isMenuCollapse = !this.isMenuCollapse;
    this.accessTokenService.sendValue(this.isMenuCollapse);
  }

  selectMenuAndRoute(menuLink: string) {
    this.selectMenuWithLink(menuLink);
    this.routeToLink(menuLink);
    // Auto-collapse sidebar after navigation if it is currently expanded
    if (!this.isMenuCollapse) {
      this.collapseMenu();
    }
  }

  selectMenuWithLink(link: string) {
    if (!this.menu) return;

    // Clear all selections
    this.menu.forEach((item: any) => {
      item.selected = false;
      if (item.children) {
        item.children.forEach((child: any) => (child.selected = false));
      }
    });

    // Special cases: routes that don't match their sidebar link directly
    if (['edit-highlight-contract', 'edit-contract', 'add-new-contract', 'view-contract', 'find-in-contract'].includes(link)) {
      this.menu.forEach((item: any) => { if (item.link === 'contracts') item.selected = true; });
      return;
    }
    if (link === 'resellers') {
      this.menu.forEach((item: any) => { if (item.link === 'ProductServices') item.selected = true; });
      return;
    }
    if (link.startsWith('users/')) {
      this.menu.forEach((item: any) => { if (item.link === 'users') item.selected = true; });
      return;
    }

    // Generic match: top-level → child
    for (const item of this.menu) {
      if (item.link === link) {
        item.selected = true;
        return;
      }
      if (item.children) {
        for (const child of item.children) {
          if (child.link === link) {
            item.selected = true;
            item.expanded = true;
            child.selected = true;
            return;
          }
        }
      }
    }

    // Fallback: a parent entry whose route redirects to a child path — e.g.
    // `admin-console` redirects to `admin-console/audit`, so the resolved URL
    // never equals the bare `admin-console` link and the exact match above
    // misses it. Match on the first path segment so the parent still
    // highlights. Runs only after exact matching finds nothing, so nested
    // child links like `foo/bar` keep priority over a parent prefix.
    const firstSegment = link.split('/')[0];
    if (firstSegment && firstSegment !== link) {
      for (const item of this.menu) {
        if (item.link === firstSegment) {
          item.selected = true;
          return;
        }
      }
    }
  }

  toggleSubmenu(item: any) {
    // Collapsed rail + parent with children → expand the rail AND open this
    // parent's submenu inline so the user can navigate. Round-trips back to
    // collapsed automatically once they pick a child (see selectMenuAndRoute).
    if (this.isMenuCollapse && item.children) {
      this.menu.forEach((m: any) => { if (m !== item) m.expanded = false; });
      item.expanded = true;
      this.collapseMenu();
      return;
    }
    if (item.children && !this.isMenuCollapse) {
      item.expanded = !item.expanded;
    } else {
      this.selectMenuAndRoute(item.link);
    }
  }

  routeToLink(link: string) {
    this.router.navigateByUrl(link);
  }

  buildMenuTree(data: any[]): any[] {
    // Sidebar visibility is now purely data-driven: sidebar entries are
    // top-level tblPage rows with IndexNo set. Sub-feature gates (tabs,
    // sections, drill-in routes) live in tblPageFields on their owning
    // parent page and are read via DynamicDataService.getStatusFieldName —
    // they never reach this cache.
    const topLevel = data.filter((item: any) => !item.ParentId);
    return topLevel.map((item: any) => {
      const children = data.filter((c: any) => c.ParentId != null && +c.ParentId === +item.Id);
      return {
        id: item.Id,
        title: item.Title,
        tooltip: item.Tooltip,
        icon: item.Icon,
        link: item.Link,
        selected: false,
        expanded: false,
        children: children.length > 0 ? children.map((child: any) => ({
          id: child.Id,
          title: child.Title,
          tooltip: child.Tooltip,
          icon: child.Icon,
          link: child.Link,
          selected: false,
        })) : undefined,
      };
    });
  }

  getSidebarData() {
    const requestBody = {
      UserID: this.accessTokenService.getUserId()
    };
    this.dynamicDataService.getSidebarData(requestBody).subscribe({
      next: (res: any) => {
        if (res['status'] == 'success') {
          this.dynamicDataService.setDataToLocal('SideBar', res['data']);
          // The tab title is labelled from this cache; the navigation that
          // got us here ran before it existed.
          this.titleStrategy.updateTitle(this.router.routerState.snapshot);
          this.aMenuList = this.buildMenuTree(res['data']);
          this.menu = this.aMenuList;
          this.appendStaticMenuItems();
          this.injectDevMenuItems();
          this.captureMenuForGating();
          const initialPath = this.router.url.replace(/^\//, '').split('?')[0];
          this.selectMenuWithLink(initialPath || 'home');
        } else {
          console.error('Sidebar API returned non-success status:', res['status']);
          this.menu = [{ title: 'Home', tooltip: 'Home', icon: 'home', link: 'home', selected: true }];
          this.appendStaticMenuItems();
          this.injectDevMenuItems();
          this.captureMenuForGating();
        }
      },
      error: () => {
        console.error('Failed to load sidebar data');
        this.menu = [{ title: 'Home', tooltip: 'Home', icon: 'home', link: 'home', selected: true }];
        this.appendStaticMenuItems();
        this.injectDevMenuItems();
        this.captureMenuForGating();
      }
    });
  }

  /**
   * Injects local-only dev-menu items into the sidebar when running on an
   * allowed dev/test server. Items are sourced from `side-bar.dev-menu.ts`,
   * which ships empty by default — see that file for the skip-worktree
   * recipe that keeps personal entries out of commits.
   */
  injectDevMenuItems() {
    if (!ALLOWED_SERVERS.includes(window.location.host)) return;
    if (!DEV_MENU_ITEMS.length) return;
    if (!this.menu) this.menu = [];

    for (const item of DEV_MENU_ITEMS) {
      const alreadyInMenu = this.menu.some((m: any) => m.link === item.link ||
        (m.children && m.children.some((c: any) => c.link === item.link)));
      if (!alreadyInMenu) {
        this.menu.push(item);
      }
    }
  }

  /**
   * Snapshots the fully assembled menu, then applies the RBAC gates. The
   * snapshot (not `this.menu`) is the filter source so an entry hidden by the
   * default-deny first emission comes back once hydration allows it.
   */
  private captureMenuForGating() {
    this.fullMenu = this.menu;
    this.applyPermissionGates();
  }

  private applyPermissionGates() {
    if (!this.fullMenu) return;
    this.menu = this.fullMenu.filter((m: any) => {
      // Internal-only surfaces (e.g. /savings-settings) are hidden entirely
      // unless the caller is staff at the link's level. See
      // INTERNAL_ONLY_SIDEBAR_LINKS docblock.
      const staffLevel = INTERNAL_ONLY_SIDEBAR_LINKS.get(m.link);
      if (staffLevel && !this.accessTokenService.isSpendkeyStaff(staffLevel)) return false;
      // Existing per-link RBAC gate (control-tower, savings-opportunity).
      if (this.deniedLinks.has(m.link)) return false;
      return true;
    });
  }

  appendStaticMenuItems() {
    if (!this.menu) this.menu = [];
    // Attachments demo and Admin Console are now DB-driven sidebar entries
    // (tblPage rows 'attachments-demo' / 'admin-console', currently Status=0),
    // so they no longer come from the STATIC_MENU_ITEMS / SUPER_ADMIN_MENU_ITEMS
    // client-side arrays. Kept commented (not deleted) so the migration is
    // reversible; flip the tblPage Status to surface either page.
    // for (const item of STATIC_MENU_ITEMS) {
    //   const alreadyInMenu = this.menu.some((m: any) => m.link === item.link ||
    //     (m.children && m.children.some((c: any) => c.link === item.link)));
    //   if (!alreadyInMenu) {
    //     this.menu.push({ ...item });
    //   }
    // }

    // Admin-console entries (US #9320 / F5 #9348). Hidden for everyone else
    // here; the parent route is independently gated by `superAdminGuard`, which
    // applies the same check.
    //
    // Gated by `AccessTokenService.canAccessAdminConsole()` — by design, every
    // authenticated Spendkey-staff user (`@spendkey.io` or `@spendkey.co.uk`) is
    // treated as a SuperAdmin for the internal admin console (the Spendkey domain
    // IS the SuperAdmin check). See the docblock on `canAccessAdminConsole()` for
    // the policy rationale and the documented tighten-back path.
    // if (this.accessTokenService.canAccessAdminConsole()) {
    //   for (const item of SUPER_ADMIN_MENU_ITEMS) {
    //     const alreadyInMenu = this.menu.some((m: any) => m.link === item.link ||
    //       (m.children && m.children.some((c: any) => c.link === item.link)));
    //     if (!alreadyInMenu) {
    //       this.menu.push({ ...item });
    //     }
    //   }
    // }
  }

  getSettings() {
    const requestBody = {
      Organization_Id: this.accessTokenService.getOrganizationId(),
      UserId: this.accessTokenService.getUserId(),
    };
    this.settingsService.getSettings(requestBody).subscribe((response: any) => {
      if (response.status == 'success') {
        this.settingsTable = response.data;
        if (this.settingsTable.find((setting: any) => setting.SettingName == 'DefaultFinancialYear') != undefined) {
          this.selectedYr = this.settingsTable.find((setting: any) => setting.SettingName == 'DefaultFinancialYear').SettingValue;
        }
        this.clientIcon = this.settingsTable.find((setting: any) => setting.SettingName == 'ClientIcon')?.SettingValue || 'assets/logo-white.svg';
        localStorage.setItem('ClientIcon', this.clientIcon);
        this.clientNm = this.settingsTable.find((setting: any) => setting.SettingName == 'ClientName')?.SettingValue || 'Spendkey';
        localStorage.setItem('ClientName', this.clientNm);
        this.tenantConfig.setReportingCurrency(
          this.settingsTable.find((setting: any) => setting.SettingName == 'ReportingCurrency')?.SettingValue,
        );
        this.tenantConfig.setSidebarMetrics({
          SidebarWidth: this.settingsTable.find((setting: any) => setting.SettingName == 'SidebarWidth')?.SettingValue,
          SidebarLabelSize: this.settingsTable.find((setting: any) => setting.SettingName == 'SidebarLabelSize')?.SettingValue,
          SidebarLabelLines: this.settingsTable.find((setting: any) => setting.SettingName == 'SidebarLabelLines')?.SettingValue,
        });
        this.getBudgetYears();
      }
    });
  }

  getBudgetYears() {
    const req = {
      Organization_Id: this.accessTokenService.getOrganizationId(),
    };
    this.budgetService.getCreatedBudgetYears(req).subscribe((response: any) => {
      if (response.status == 'success') {
        this.createdBgtYr = response.data;
        this.selectedYr = this.settingsTable.find((setting: any) => setting.SettingName == 'DefaultFinancialYear').SettingValue;
        this.toSelect = this.createdBgtYr.find((c: any) => c.Id == this.selectedYr);
        const dataToStore = { key: this.toSelect };
        localStorage.setItem('FinancialYear', JSON.stringify(dataToStore));
      }
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalId);
    this.permGateSub.unsubscribe();
    this.collapseSub.unsubscribe();
    this.trackedEvents.forEach(e => window.removeEventListener(e, this.activityHandler));
  }
}
